import { useEffect, useRef, useState } from "react";
import { BASE_URL } from "../data/constants";
import { getToken } from "../Services/authStore";
import { useVenueGate } from "./useVenueGate";
import { updateVenueNowPlaying } from "../Services/VenueService";

type Match = { title?: string; artist?: string; confidence?: number };

export type RecognizeResponse = {
  ok?: boolean;
  match?: Match | null;
  raw?: any;
  error?: string;
};

export type Mood = "calm" | "nice" | "lit" | "wild" | null;

export type Step =
  | "checking"
  | "blocked"
  | "pickVenue"
  | "ready"
  | "recording"
  | "uploading"
  | "result"
  | "mood"
  | "posting"
  | "done"
  | "error";

/**
 * Omslaget kommer från AudD:s egen song_link, inte från Spotify eller Apple.
 * Saknas länken får låten vara utan bild.
 */
function extractCover(raw: any): string | null {
  const songLink = raw?.result?.song_link;
  if (!songLink) return null;
  return String(songLink).includes("?") ? `${songLink}&thumb` : `${songLink}?thumb`;
}

export function useRecognizeFlow() {
  const { gate, precheck, selectVenue, getCtx } = useVenueGate();

  const [step, setStep] = useState<Step>("checking");
  const [result, setResult] = useState<RecognizeResponse | null>(null);
  const [mood, setMood] = useState<Mood>(null);
  const [posting, setPosting] = useState(false);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);

  const cover = extractCover(result?.raw);

  const refreshGate = async () => {
    setStep("checking");
    setResult(null);

    const check = await precheck();

    if (!check.ok) {
      setStep("blocked");
      setResult({ error: check.reason });
      return { ok: false as const, reason: check.reason };
    }

    const ctx = getCtx();
    const hasVenue = !!ctx.venueId;

    if (check.candidates.length > 1 && !hasVenue) {
      setStep("pickVenue");
      return { ok: true as const, state: "pickVenue" as const };
    }

    setStep("ready");
    return { ok: true as const, state: "ready" as const };
  };

  // auto gate on mount
  useEffect(() => {
    let alive = true;
    (async () => {
      const r = await refreshGate();
      if (!alive) return;
      return r;
    })().catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chooseVenue = (venueId: string) => {
    selectVenue(venueId);
    setStep("ready");
  };

  const stop = () => recorderRef.current?.stop();

  const startRecognize = async () => {
    setResult(null);
    setMood(null);

    // refresh gate right before (token may expire)
    const check = await precheck();
    if (!check.ok) {
      setStep("blocked");
      setResult({ error: check.reason });
      return;
    }

    const ctxNow = getCtx();
    if (!ctxNow.venueId) {
      setStep("pickVenue");
      setResult({ error: "choose_venue_first" });
      return;
    }

    // mic + record
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;

    const rec = new MediaRecorder(stream);
    recorderRef.current = rec;
    chunksRef.current = [];

    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };

    rec.onstop = async () => {
      try {
        setStep("uploading");

        const token = getToken();
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });

        const form = new FormData();
        form.append("audio", blob, `snippet.${blob.type.includes("webm") ? "webm" : "bin"}`);

        const ctx = getCtx();
        if (ctx.pos) {
          form.append("lat", String(ctx.pos.lat));
          form.append("lon", String(ctx.pos.lon));
          form.append("accuracy", String(ctx.pos.accuracy));
          form.append("ts", String(ctx.pos.timestamp));
        }
        if (ctx.venueId) form.append("venueId", ctx.venueId);
        if (ctx.selectionToken) form.append("selectionToken", ctx.selectionToken);

        const r = await fetch(BASE_URL + "/api/recognize", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        });

        const data = await r.json();
        setResult(data);

        if (data?.match) {
          setStep("result");
        } else {
          // setResult(data) ovan gör prev sanningsenligt, så ?? föll aldrig
          // tillbaka och result.error förblev undefined. Felvyn renderas
          // bara när result.error finns, så sidan blev helt tom.
          setStep("error");
          setResult({ ...data, error: data?.error ?? "no_match" });
        }
      } catch (e: any) {
        setStep("error");
        setResult({ error: e?.message ?? "recognize_failed" });
      } finally {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };

    setStep("recording");
    rec.start();
    setTimeout(() => rec.stop(), 10_000);
  };

  // after result, go to mood step (optional)
  const goToMood = () => {
    if (result?.match) setStep("mood");
  };

  const postToVenue = async (maybeMood?: Mood) => {
    const ctx = getCtx();

    if (!ctx.venueId) {
      setStep("pickVenue");
      setResult({ error: "choose_venue_first" });
      return;
    }
    if (!result?.match) {
      setStep("error");
      setResult({ error: "no_match" });
      return;
    }
    if (!ctx.selectionToken || !ctx.pos) {
      setStep("error");
      setResult({ error: "venue_gate_missing" });
      return;
    }

    try {
      setPosting(true);
      setStep("posting");

      const audd = result.raw?.result;

      // Metadata från AudD:s top-level-fält. previewUrl hålls separat och är
      // frivillig — en tillfällig MVP-fallback från tredje part.
      const previewUrl = audd?.spotify?.preview_url ?? null;

      // NOTE: mood skickas inte i din backend än.
      // Du kan antingen lägga det i track-payload (inte idealiskt),
      // eller uppdatera backend senare och skicka med mood här.
      const res = await updateVenueNowPlaying({
        venueId: ctx.venueId,
        track: {
          title: result.match.title,
          artist: result.match.artist,
          album: audd?.album ?? null,
          songLink: audd?.song_link ?? null,
          year: audd?.release_date?.slice(0, 4) ?? null,
          previewUrl,
        },
        selectionToken: ctx.selectionToken,
        lat: ctx.pos.lat,
        lon: ctx.pos.lon,
        // mood: maybeMood ?? mood  <-- när backend stödjer
      });

      if (!res.ok) {
        setStep("error");
        setResult({ error: res.error });
        return;
      }

      setStep("done");
    } finally {
      setPosting(false);
    }
  };

  return {
    // data
    gate,
    ctx: getCtx(),
    step,
    result,
    cover,
    mood,
    posting,

    // actions
    refreshGate,
    chooseVenue,
    startRecognize,
    stop,
    setMood,
    goToMood,
    postToVenue,
    setStep,
  };
}
